import { useDispatch, useInvestment } from '@/hooks/useStore';
import Dialog, {
  DefaultDialogActions,
  DialogBody,
  DialogTitle,
} from '@/components/Dialog';
import { API } from '@/api';
import { deleteInvestment as dispatchDeleteInvestment } from '@/store/slices/investmentsSlice';
import { showSuccessToaster } from '@/utils/toasters';
import { useAppState } from '@/hooks/useAppState';
import { useToggle } from '@/hooks/useToggle';
import { classes } from './utils';

export type DeleteInvestmentDialog = {
  investmentId: number;
  onDelete?: (investmentId: number) => void;
  onClose: () => void;
};

export default function DeleteInvestmentDialog({
  investmentId,
  onDelete,
  onClose,
}: DeleteInvestmentDialog) {
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const investment = useInvestment(investmentId);
  const [isDeleting, toggleDeleting] = useToggle(false);

  const handleDelete = async () => {
    try {
      toggleDeleting(true);
      await API.Investments.removeInvestment(investmentId);
      if (onDelete) onDelete(investmentId);
      dispatch(dispatchDeleteInvestment({ investmentId }));
      showSuccessToaster('Investment removed');
      toggleDeleting(false);
      onClose();
    } catch (err) {
      toggleDeleting(false);
      onApiError(err, 'Error removing investment', () => {
        handleDelete();
      });
    }
  };

  if (!investment) return null; // TODO: Show an error message

  return (
    <Dialog className={classes.dialog} onClose={onClose}>
      <DialogTitle title="Remove investment" onClose={onClose} />
      <DialogBody className={classes.body}>
        <p className={classes.paragraph}>
          Are you sure you want to remove this investment?
          <br />
          This action cannot be undone.
        </p>
      </DialogBody>
      <DefaultDialogActions
        pending={isDeleting}
        confirmLabel="Remove"
        pendingLabel="Removing..."
        onCancel={onClose}
        onConfirm={handleDelete}
      />
    </Dialog>
  );
}
